import { useEffect, useMemo } from "react";
import type * as React from "react";
import { Box, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { PATHS } from "constants/pathConstants";
import { FORM_IDS } from "constants/v3/formConstants";
import PageTitle from "utils/PageTitle";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import BreadcrumbTrail from "components/v3/sharedComponents/BreadcrumbTrail";
import FormTemplateWrapper from "components/v3/sharedComponents/forms/FormTemplateWrapper";
import {
  AGREEMENT_FORM_TEMPLATE_ROOT_CLASS,
  getAgreementFormSurveyScopeSx
} from "components/v3/sharedComponents/forms/agreementFormSurveyScopeSx";
import AccountSecurityCallout from "components/v3/pages/account/AccountManagementPage/AccountSecurityCallout";
import {
  selectHasAcceptedCurrentHipaaAuthorization,
  selectHasAcceptedCurrentPrivacyPolicy,
  selectHasAcceptedCurrentTermsAndConditions
} from "store/reducers/formTemplates/FormTemplatesSlice";

export type AgreementReviewPageProps = {
  formId: string;
  title: string;
};

const TERMS_OF_SERVICE_HUB_LABEL = "Terms of Service";

const agreementReviewFormScopeClass = "agreement-review-form-scope";

const agreementReviewPageColumnSx = {
  boxSizing: "border-box",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: 3,
  height: { md: "calc(100dvh - 72px)", xs: "calc(100dvh - 64px)" },
  marginX: "auto",
  maxWidth: { md: 840, xs: "100%" },
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  paddingBottom: { md: 3, xs: 2 },
  paddingTop: { md: 3, xs: 2 },
  paddingX: { md: 3, xs: 2 },
  width: "100%"
} as const;

const agreementReviewFormScopeSx = getAgreementFormSurveyScopeSx({
  scrollContentMaxHeight: "calc(min(100vh, 100dvh) - 400px) !important",
  paddingLeft: { md: 4, sm: 2.5, xs: 2 }
});

const AgreementReviewPage: React.FC<AgreementReviewPageProps> = ({
  formId,
  title
}) => {
  const history = useHistory();
  const privacyAccepted = useSelector(selectHasAcceptedCurrentPrivacyPolicy);
  const termsAccepted = useSelector(selectHasAcceptedCurrentTermsAndConditions);
  const hipaaAccepted = useSelector(selectHasAcceptedCurrentHipaaAuthorization);

  const alreadyAccepted = useMemo(() => {
    switch (formId) {
      case FORM_IDS.PRIVACY_POLICY:
        return privacyAccepted;
      case FORM_IDS.TERMS_AND_CONDITIONS:
        return termsAccepted;
      case FORM_IDS.HIPAA_AUTHORIZATION:
        return hipaaAccepted;
      default:
        return false;
    }
  }, [formId, hipaaAccepted, privacyAccepted, termsAccepted]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [formId]);

  PageTitle(title);

  const handleComplete = () => {
    history.push(PATHS.ACCOUNT_MANAGEMENT_AGREEMENTS);
  };

  return (
    <GradientFramedLayout showHeader={true}>
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: { md: "calc(100dvh - 72px)", xs: "calc(100dvh - 64px)" },
          minWidth: 0,
          width: "100%"
        }}
      >
        <Box sx={agreementReviewPageColumnSx}>
          <Typography
            variant="h4_primary"
            component="h1"
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              textAlign: "left",
              width: "100%"
            }}
          >
            {title}
          </Typography>

          <Box sx={{ flexShrink: 0, width: "100%" }}>
            <BreadcrumbTrail
              segments={[
                { label: "Account", to: PATHS.ACCOUNT_MANAGEMENT },
                {
                  label: "Privacy & Security",
                  to: PATHS.ACCOUNT_MANAGEMENT_PRIVACY_AND_SECURITY
                },
                {
                  label: TERMS_OF_SERVICE_HUB_LABEL,
                  to: PATHS.ACCOUNT_MANAGEMENT_AGREEMENTS
                },
                { label: title }
              ]}
            />
          </Box>

          <Box
            className={agreementReviewFormScopeClass}
            sx={agreementReviewFormScopeSx}
          >
            <FormTemplateWrapper
              formId={formId}
              onComplete={handleComplete}
              preCheckConfirmationQuestion={alreadyAccepted}
              templateRootClassName={AGREEMENT_FORM_TEMPLATE_ROOT_CLASS}
            />
          </Box>

          <Box sx={{ flexShrink: 0, width: "100%" }}>
            <AccountSecurityCallout />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default AgreementReviewPage;
